import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Search, Sparkles, ShieldCheck, ArrowRight, Zap, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { SiteHeader, SiteFooter } from "@/components/tools/ToolShell";
import { DE_CALCULATOR_CATEGORIES, DE_CALCULATORS, type DeCalculatorCategory } from "@/lib/de-calculators-catalog";
import { CalcIcon } from "@/components/calculators/CalcIcon";

export const Route = createFileRoute("/de/calculators/")({
  head: () => ({
    meta: [
      { title: "Kostenlose Online-Rechner — 122+ Mathe-, Finanz- & Notenrechner | Picamini.online" },
      {
        name: "description",
        content:
          "Kostenlose Online-Rechner für Schüler, Studenten, Lehrer und Berufstätige. Prozentrechner, Hypothekenrechner, BMI-Rechner, Notenrechner und Mathe-Tools ohne Registrierung.",
      },
      {
        name: "keywords",
        content:
          "online rechner, prozentrechner, notenrechner, bmi rechner, hypothekenrechner, mathe tools kostenlos",
      },
      { property: "og:site_name", content: "Picamini.online" },
      { property: "og:title", content: "Kostenlose Online-Rechner — 122+ Sofort-Tools" },
      {
        property: "og:description",
        content:
          "Schnelle, genaue und 100% datenschutzfreundliche Online-Rechner direkt im Browser.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://picamini.online/de/calculators" },
      { property: "og:image", content: "https://picamini.online/og-image.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:locale", content: "de_DE" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Kostenlose Online-Rechner — 122+ Tools" },
      { name: "twitter:description", content: "Kostenlose Online-Rechner für Mathematik, Finanzen, Fitness und Physik." },
      { name: "twitter:image", content: "https://picamini.online/og-image.png" },
      { name: "theme-color", content: "#4F46E5" },
      { name: "robots", content: "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" },
    ],
    links: [
      { rel: "canonical", href: "https://picamini.online/de/calculators" },
      { rel: "alternate", hrefLang: "en", href: "https://picamini.online/calculators" },
      { rel: "alternate", hrefLang: "es", href: "https://picamini.online/es/calculators" },
      { rel: "alternate", hrefLang: "de", href: "https://picamini.online/de/calculators" },
      { rel: "alternate", hrefLang: "fr", href: "https://picamini.online/fr/calculators" },
      { rel: "alternate", hrefLang: "pt-BR", href: "https://picamini.online/pt-br/calculators" },
      { rel: "alternate", hrefLang: "x-default", href: "https://picamini.online/calculators" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Verzeichnis kostenloser Online-Rechner",
          "url": "https://picamini.online/de/calculators",
          "inLanguage": "de",
          "description": "Übersicht über 122+ kostenlose, clientseitige Online-Rechner für Mathe, Finanzen, Noten und Fitness.",
          "mainEntity": {
            "@type": "ItemList",
            "numberOfItems": DE_CALCULATORS.length,
            "itemListElement": DE_CALCULATORS.slice(0, 20).map((c, index) => ({
              "@type": "ListItem",
              "position": index + 1,
              "name": c.name,
              "url": `https://picamini.online/de/calculators/${c.slug}`,
              "description": c.tagline
            }))
          }
        })
      }
    ]
  }),
  component: DeCalculatorsHub,
});

export function DeCalculatorsHub() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<DeCalculatorCategory | "Alle">("Alle");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return DE_CALCULATORS.filter((c) => {
      const matchesCategory = category === "Alle" || c.category === category;
      const matchesQuery =
        !q ||
        c.name.toLowerCase().includes(q) ||
        c.tagline.toLowerCase().includes(q) ||
        c.keywords.some((k) => k.includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [query, category]);

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader lang="de" />
      <main>
        {/* Hero Section */}
        <section className="bg-hero-gradient text-primary-foreground">
          <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-16 sm:py-20 text-center">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3.5 py-1 text-xs font-bold uppercase tracking-wider backdrop-blur mb-4">
              <ShieldCheck className="size-4 text-emerald-300" />
              100% Kostenlos & Sicher — Direkte Browser-Berechnung
            </div>
            <h1 className="text-3xl font-black tracking-tight sm:text-5xl lg:text-6xl">
              Kostenlose Online-Rechner
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-base sm:text-lg text-primary-foreground/80 leading-relaxed">
              Schnelle, präzise und praktische Online-Rechner für Schule, Studium und Alltag.
              Berechne Prozentwerte, Noten, Kredite, BMI und mehr in Sekundenschnelle.
            </p>

            {/* Live Search Bar */}
            <div className="mx-auto mt-8 max-w-xl">
              <div className="relative flex items-center">
                <Search className="absolute left-4 size-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Rechner suchen (z.B. Prozent, Noten, Hypothek)..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-13 pl-12 pr-4 rounded-2xl bg-background/95 text-foreground shadow-lg backdrop-blur text-sm sm:text-base border-white/20 focus-visible:ring-2 focus-visible:ring-primary"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Catalog Section */}
        <section className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-8 py-12">
          {/* Category Tabs */}
          <div className="flex flex-wrap items-center gap-2 border-b border-border/80 pb-4">
            <Button
              variant={category === "Alle" ? "default" : "outline"}
              size="sm"
              onClick={() => setCategory("Alle")}
              className="rounded-full text-xs font-bold"
            >
              Alle ({DE_CALCULATORS.length})
            </Button>
            {DE_CALCULATOR_CATEGORIES.map((cat) => (
              <Button
                key={cat}
                variant={category === cat ? "default" : "outline"}
                size="sm"
                onClick={() => setCategory(cat)}
                className="rounded-full text-xs font-bold"
              >
                {cat}
              </Button>
            ))}
          </div>

          {/* Calculator Grid */}
          <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((calc) => (
              <Link
                key={calc.slug}
                to={`/de/calculators/${calc.slug}`}
                className="group relative flex flex-col justify-between rounded-3xl border border-border/80 bg-card p-6 shadow-xs transition-all hover:-translate-y-1 hover:border-primary/50 hover:shadow-xl"
              >
                <div>
                  <div className="flex items-center justify-between">
                    <div className="grid size-12 place-items-center rounded-2xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                      <CalcIcon name={calc.icon} className="size-6" />
                    </div>
                    {calc.featured && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[11px] font-extrabold uppercase tracking-wider text-amber-600 dark:text-amber-400 border border-amber-500/20">
                        <Sparkles className="size-3" /> Empfohlen
                      </span>
                    )}
                  </div>

                  <h3 className="mt-5 text-xl font-bold text-foreground transition-colors group-hover:text-primary">
                    {calc.name}
                  </h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {calc.tagline}
                  </p>
                </div>

                <div className="mt-6 flex items-center justify-between border-t border-border/60 pt-4 text-xs font-bold text-primary">
                  <span>Rechner öffnen</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-lg font-bold text-foreground">Keine Rechner gefunden</p>
              <p className="text-sm text-muted-foreground mt-1">Versuche einen anderen Suchbegriff oder eine andere Kategorie.</p>
            </div>
          )}
        </section>

        {/* Informational Section */}
        <section className="border-t border-border/60 bg-secondary/15 py-16">
          <div className="mx-auto max-w-5xl px-4 sm:px-6">
            <article className="prose prose-slate dark:prose-invert max-w-none space-y-12">
              <header className="space-y-3">
                <span className="text-xs font-bold uppercase tracking-widest text-primary">
                  Präzise, Sichere & Kostenlose Online-Berechnung
                </span>
                <h2 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl text-foreground">
                  Die umfassende Sammlung von Online-Rechnern
                </h2>
                <p className="text-base text-muted-foreground leading-relaxed">
                  Picamini.online bietet Ihnen <strong>122+ kostenlose Rechner</strong> für mathematische Gleichungen, Zinseszins-Berechnungen, Notendurchschnitte und Gesundheitstracking mit 100% Datenschutz.
                </p>
              </header>

              <div className="grid gap-6 sm:grid-cols-3 not-prose">
                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <ShieldCheck className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">100% Datenschutz</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Alle Berechnungen laufen lokal im Browser ab. Keine Datenübertragung.</p>
                </div>
                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <Zap className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Echtzeit-Ergebnisse</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Sofortige Berechnung während des Tippens inklusive Rechenweg.</p>
                </div>
                <div className="rounded-2xl border border-border/80 bg-card p-6 shadow-xs">
                  <div className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary mb-4">
                    <CheckCircle2 className="size-5" />
                  </div>
                  <h3 className="text-lg font-bold text-foreground">Ohne Anmeldung</h3>
                  <p className="mt-2 text-sm text-muted-foreground">Kostenlose und unbegrenzte Nutzung ohne Registrierung.</p>
                </div>
              </div>
            </article>
          </div>
        </section>
      </main>
      <SiteFooter lang="de" />
    </div>
  );
}
